import type { Category, ScoringSex } from '../scoring/types';

export interface Workout {
  id: string;
  userId: string;
  workoutDate: string; // YYYY-MM-DD, Los Angeles
  monthKey: string; // YYYY-MM
  category: Category;
  exerciseId: string;
  exerciseNameSnapshot: string;

  enteredWeightLb: number;
  reps: number;
  inputWeightMultiplier: number;
  /** Fraction of body weight included in the load (0 for external-only lifts). */
  bodyWeightFactor?: number;

  estimated1RmLb: number;
  normalizationMultiplier: number;
  normalized1RmLb: number;

  bodyWeightLbSnapshot: number;
  scoringSexSnapshot: ScoringSex;
  dotsCoefficient: number;
  categoryReferenceDots: number;
  points: number;

  scoringVersion: string;
  exerciseConfigVersion: string;

  note?: string;
  idempotencyKey: string;

  createdAt: Date;
  updatedAt: Date;
}

export interface CreateWorkoutInput {
  userId: string;
  exerciseId: string;
  exerciseName: string;
  category: Category;
  enteredWeightLb: number;
  reps: number;
  inputWeightMultiplier: number;
  normalizationMultiplier: number;
  bodyWeightFactor?: number;
  workoutDate: string; // YYYY-MM-DD
  bodyWeightLb: number;
  scoringSex: ScoringSex;
  note?: string;
  exerciseConfigVersion: string;
}

export interface MonthlyBest {
  category: Category;
  workoutId: string;
  points: number;
  normalized1RmLb: number;
  /** This lift's own 1RM, before normalising to the competition lift. */
  estimated1RmLb: number;
  exerciseId: string;
  exerciseName: string;
  enteredWeightLb: number;
  reps: number;
  /** Carried so the load can be phrased correctly - see describeLift.ts. */
  inputWeightMultiplier?: number;
  bodyWeightFactor?: number;
}

export interface MonthlySummary {
  pushBest: MonthlyBest | null;
  /** Upper-body pulling (displayed "Pull"). */
  rowBest: MonthlyBest | null;
  /** Deadlift category (displayed "Hinge"). */
  hingeBest: MonthlyBest | null;
  squatBest: MonthlyBest | null;
  totalPoints: number;
  isIncomplete: boolean;
}

export function getMonthKey(dateStr: string): string {
  return dateStr.substring(0, 7);
}

export function generateIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for environments where crypto.randomUUID is not available
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function isCurrentMonth(monthKey: string): boolean {
  return monthKey === getCurrentMonthKey();
}

export function getLADate(): Date {
  const now = new Date();
  const laTimeString = now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' });
  return new Date(laTimeString);
}

export function getLADateString(): string {
  const laDate = getLADate();
  const year = laDate.getFullYear();
  const month = String(laDate.getMonth() + 1).padStart(2, '0');
  const day = String(laDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getCurrentMonthKey(): string {
  const laDate = getLADate();
  const year = laDate.getFullYear();
  const month = String(laDate.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}
